import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Link } from 'gatsby'
import { AnchorLink } from 'gatsby-plugin-anchor-links'
import {
  AffinityProvider,
  Box,
  Button,
  Container,
  Flex,
  Icon,
  Paragraph,
  P,
  Ul,
  H3,
  Span,
} from '@allied-solutions/affinity'
import { ArrowRight } from '@allied-solutions/affinity-react-icons/dist/standard'

import { useSiteMetadata } from '../../hooks/useSiteMetadata'
import { dark } from '../../theme.js'
import { Logo } from '../Logo'
import { FooterGroup } from '../FooterGroup'

// eliminate console warning for 'fancy' variant
Button.propTypes = {
  ...Button.propTypes,
  variant: PropTypes.oneOf(Object.keys(dark.buttons)),
}

const footerLinkStyles = `
  &&,
  &&:link,
  &&:visited,
  &&:hover,
  &&:focus,
  &&:active {
    text-decoration: none;
    color: #ffffff;
  }
`

const FooterGatsbyLink = styled(Link)`
  ${footerLinkStyles}
`

const FooterExternalLink = styled.a`
  ${footerLinkStyles}
`

const NavHeader = ({ children }) => (
  <P
    typeStyle={['labelLarge.bold', 'bodySmall']}
    textAlign="center"
    color="teal200"
    fontWeight="bold"
    display="block"
    mb={4}
    css={`
      text-transform: uppercase;
    `}
  >
    {children}
  </P>
)

const SubMenu = ({ children }) => (
  <Ul
    css={`
      li + li {
        margin-top: ${props => props.theme.space[1]};
      }
    `}
  >
    {children}
  </Ul>
)

const SubMenuItem = ({ slug, children }) => (
  <li>
    <FooterGatsbyLink id={`Global__Footer__Link--${slug}`} to={`/${slug}/`}>
      <P mb={0} color={'#FFFFFF'} typeStyle={['bodyLarge', 'bodyMedium']}>
        {children}
      </P>
    </FooterGatsbyLink>
  </li>
)

const BottomOfFooter = () => {
  const { alliedInfo, copyrightText } = useSiteMetadata()
  return (
    <>
      <Box pt={20} pb={[24, 24, '9xl']}>
        <nav aria-label="Site footer navigation">
          <Ul
            display={[null, null, 'flex']}
            css={`
              li {
                flex: 1;
              }

              > li + li {
                margin-top: ${props => props.theme.space[8]};
              }

              ${props => props.theme.mediaQueries.md} {
                > li + li {
                  margin-top: 0;
                }
              }
            `}
          >
            <li>
              <NavHeader>Learn More</NavHeader>
              <SubMenu>
                <SubMenuItem slug="support">Support</SubMenuItem>
                <SubMenuItem slug="digital-accessibility">
                  Digital Accessibility
                </SubMenuItem>
              </SubMenu>
            </li>
            <li>
              <NavHeader>Trust and Security</NavHeader>
              <SubMenu>
                <SubMenuItem slug="terms-conditions">
                  Terms & Conditions
                </SubMenuItem>
                <SubMenuItem slug="privacy-policy">Privacy Policy</SubMenuItem>
              </SubMenu>
            </li>
            <li>
              <NavHeader>About Allied</NavHeader>
              <SubMenu>
                <li>
                  <FooterExternalLink
                    id="Global__Footer__Link--allied-solutions"
                    href={alliedInfo.siteUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Allied Solutions - Home Page"
                  >
                    <P mb={0} color="#FFFFFF" typeStyle="bodyMedium">
                      Allied Solutions
                    </P>
                  </FooterExternalLink>
                </li>
                <li>
                  <FooterExternalLink
                    id="Global__Footer__Link--our-partners"
                    href="https://www.alliedsolutions.net/approach/Partners"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Allied Solutions - Our Partners"
                  >
                    <P
                      mb={0}
                      display="block"
                      textAlign="center"
                      color="#FFFFFF"
                      typeStyle="bodyMedium"
                    >
                      Our Partners
                    </P>
                  </FooterExternalLink>
                </li>
              </SubMenu>
            </li>
          </Ul>
        </nav>
      </Box>
      <Flex display={[null, null, 'flex']} alignItems="center">
        <Flex.Col
          order="2"
          pb={[10, 10, 0]}
          mb={[5, 5, 0]}
          borderBottom={[0, `1px solid`, 0]}
          borderBottomColor={['blue', 'blue', 'transparent']}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="2xl"
        >
          <Box
            as="a"
            href="https://www.alliedsolutions.net/technology/"
            target="_blank"
            rel="noopener noreferrer"
            width={['180px', '240px']}
            style={{ textDecoration: 'none', fontWeight: 500 }}
          >
            <img
              src="/allied-logo-white.svg"
              alt="Allied Solutions"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </Box>
          <Box height="1px" bg="border.defaultLight" width="115px" />
          <Logo
            id={`Global__Footer__Logo`}
            white
            css={`
              img {
                display: block;
                margin: 0 auto;
              }
            `}
          />
        </Flex.Col>
        <Flex.Col order="1" pr={[0, 0, 0, '5xl']}>
          <Paragraph
            typeStyle={[
              'labelMedium.regular',
              'labelMedium.regular',
              'labelMedium.regular',
              'bodySmall',
            ]}
            color="teal200"
            mb={0}
            textAlign={['center', 'center', 'left']}
          >
            {alliedInfo.address}
          </Paragraph>
          <Paragraph
            typeStyle="bodySmall"
            color="teal200"
            mb={0}
            textAlign={['center', 'center', 'left']}
          >
            {copyrightText}
          </Paragraph>
        </Flex.Col>
        <Flex.Col
          order="3"
          display="flex"
          justifyContent={['center', 'center', 'flex-end']}
          mt={['5xl', 0]}
        >
          <FooterGroup id={`Global__Footer__Group`} white />
        </Flex.Col>
      </Flex>
    </>
  )
}

const Footer = ({ className }) => {
  return (
    <AffinityProvider theme={dark}>
      <Box
        bg="background"
        py="8xl"
        color="white"
        textAlign="center"
        className={className}
        as="footer"
      >
        <Container px={4}>
          <H3
            typeStyle="displaySmall"
            fontWeight="900"
            css={`
              margin-bottom: ${props => props.theme.space[7]};
            `}
          >
            We're here for you
          </H3>
          <Box maxWidth={[null, null, null, '45vw']} mx="auto">
            <P
              fontSize={'18px'}
              textAlign="center"
              color="white"
              mb={12}
              lineHeight={'30px'}
              fontWeight={500}
              letterSpacing={'0.5px'}
              typeStyle="bodyLarge.medium"
              px={[0, 0, '5xl', 0]}
            >
              Still have questions? Don't worry, we can help. Chat with an agent
              or give us a call to get the answers you need.
            </P>
            <Box maxWidth="300px" mx="auto">
              <Button
                id="Global__Footer__Button--contact"
                variant="navyBlue"
                forwardedAs={AnchorLink}
                to="/support/#contactSection"
                minWidth={['100%', '100%', '308px']}
                mx="auto"
                mb="8xl"
                rounded
                size="lg"
                css={`
                  border: 1px solid #ffffff;
                  height: 56px;
                `}
              >
                <Span
                  textAlign="left"
                  color="white"
                  letterSpacing="0px"
                  height="100%"
                  display="flex"
                  alignItems="center"
                >
                  Contact us
                </Span>
                <Icon size="lg" src={ArrowRight} ml={2} color="teal800" />
              </Button>
            </Box>
          </Box>
          <Box height="1px" bg="teal200" />
          <BottomOfFooter />
        </Container>
      </Box>
    </AffinityProvider>
  )
}

export { Footer, BottomOfFooter }
